/**
 * Client API service for named notification schedule definitions.
 * Schedule dates are persisted as UTC ISO-8601 strings only.
 */
import { clientApiHttp } from './httpService';

const getTenantId = () => +window.localStorage.getItem('tenantId');

const resolveTenantId = tenantId => tenantId || getTenantId();

const basePath = (tenantId, notificationId) =>
    `/v2/client/${resolveTenantId(tenantId)}/notification/${notificationId}/schedule-definition`;

/** Convert Date values and ISO-compatible values to UTC ISO strings. */
export const toUtcIsoString = value => {
    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) throw new Error('Invalid schedule date');

    return date.toISOString();
};

/** Normalize a schedule-definition payload before sending it to the API. */
export const normalizeScheduleDefinitionPayload = ({
    id = 0,
    notificationId,
    name,
    notificationFrequencyId,
    isEnabled = true,
    notificationSchedules = [],
    notificationGroups = [],
    broadcastAll = false,
}) => ({
    id,
    notificationId,
    name,
    notificationFrequencyId,
    isEnabled,
    notificationSchedules: notificationSchedules.map(toUtcIsoString),
    notificationGroups,
    broadcastAll,
});

/** Retrieve all named schedule definitions for a notification. */
export const getScheduleDefinitions = async (tenantId, notificationId) => {
    const response = await clientApiHttp.get(
        basePath(tenantId, notificationId),
    );
    return response.data;
};

/** Retrieve one named schedule definition and its occurrence dates. */
export const getScheduleDefinition = async (
    tenantId,
    notificationId,
    scheduleDefinitionId,
) => {
    const response = await clientApiHttp.get(
        `${basePath(tenantId, notificationId)}/${scheduleDefinitionId}`,
    );
    return response.data;
};

/** Retrieve temporal history for one named schedule definition. */
export const getScheduleDefinitionHistory = async (
    tenantId,
    notificationId,
    scheduleDefinitionId,
) => {
    const response = await clientApiHttp.get(
        `${basePath(tenantId, notificationId)}/${scheduleDefinitionId}/history`,
    );
    return response.data;
};

/** Create a named schedule definition and its UTC occurrence rows. */
export const createScheduleDefinition = async (
    tenantId,
    notificationId,
    payload,
) => {
    const response = await clientApiHttp.post(
        basePath(tenantId, notificationId),
        normalizeScheduleDefinitionPayload({
            ...payload,
            notificationId,
        }),
    );
    return response.data;
};

/** Update a named schedule definition and replace only future occurrence rows. */
export const updateScheduleDefinition = async (
    tenantId,
    notificationId,
    scheduleDefinitionId,
    payload,
) => {
    const response = await clientApiHttp.put(
        `${basePath(tenantId, notificationId)}/${scheduleDefinitionId}`,
        normalizeScheduleDefinitionPayload({
            ...payload,
            id: scheduleDefinitionId,
            notificationId,
        }),
    );
    return response.data;
};

/** Disable one named schedule definition. */
export const disableScheduleDefinition = async (
    tenantId,
    notificationId,
    scheduleDefinitionId,
) => {
    const response = await clientApiHttp.put(
        `${basePath(tenantId, notificationId)}/${scheduleDefinitionId}/disable`,
    );
    return response.data;
};

/** Enable one named schedule definition. */
export const enableScheduleDefinition = async (
    tenantId,
    notificationId,
    scheduleDefinitionId,
) => {
    const response = await clientApiHttp.put(
        `${basePath(tenantId, notificationId)}/${scheduleDefinitionId}/enable`,
    );
    return response.data;
};

/** Delete one named schedule definition and its operational occurrences. */
export const deleteScheduleDefinition = async (
    tenantId,
    notificationId,
    scheduleDefinitionId,
) => {
    const response = await clientApiHttp.delete(
        `${basePath(tenantId, notificationId)}/${scheduleDefinitionId}`,
    );
    return response.data;
};
